import type { EditSnapshot, StudioAsset } from '../media/types';

const DATA_URL_LIMIT = 4 * 1024 * 1024;

export interface StoredSession extends EditSnapshot {
  loop: boolean;
  scriptText?: string;
  savedAt: number;
}

export function serializeSession(
  snapshot: EditSnapshot & { loop: boolean; scriptText?: string }
): StoredSession {
  return {
    assets: snapshot.assets.map((asset) => ({
      ...asset,
      dataUrl: asset.size <= DATA_URL_LIMIT ? asset.dataUrl : undefined,
      url: null,
    })),
    tracks: snapshot.tracks,
    loop: snapshot.loop,
    scriptText: snapshot.scriptText,
    savedAt: Date.now(),
  };
}

export function deserializeSession(raw: string): StoredSession {
  const data = JSON.parse(raw) as Partial<StoredSession>;
  if (!Array.isArray(data.assets) || !Array.isArray(data.tracks)) {
    throw new Error('Invalid session payload');
  }
  const assets: StudioAsset[] = data.assets.map((asset) => ({
    ...asset,
    url: asset.dataUrl ?? null,
  }));
  return {
    assets,
    tracks: data.tracks,
    loop: Boolean(data.loop),
    scriptText: typeof data.scriptText === 'string' ? data.scriptText : undefined,
    savedAt: data.savedAt ?? 0,
  };
}

export function loadSessionFromStorage(key: string): StoredSession | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return deserializeSession(raw);
  } catch {
    return null;
  }
}

export function saveSessionToStorage(
  key: string,
  snapshot: EditSnapshot & { loop: boolean; scriptText?: string }
): void {
  const serialized = serializeSession(snapshot);
  localStorage.setItem(key, JSON.stringify(serialized));
}

export function clearSessionFromStorage(key: string): void {
  localStorage.removeItem(key);
}
